import type { ReactNode } from "react";
import { Chip, Text } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";
import { RecommendedCard } from "./RecommendedCard";
import type { DataCardCustomization } from "./types";

type InsightDetailsContentProps = {
	cardContent: ReactNode;
} & NonNullable<DataCardCustomization["insightDetails"]>;

export const InsightDetailsContent = ({
	cardContent,
	detailedInsight,
	recommendedActions,
	followUpChips,
}: InsightDetailsContentProps) => {
	const theme = useAppTheme();

	return (
		<Box padding={16} gap={16}>
			<Box
				style={{
					borderWidth: 1,
					borderColor: theme.colors.primaryContainer,
					borderRadius: (theme as { roundness?: number }).roundness
						? (theme as { roundness: number }).roundness * 3
						: 12,
					overflow: "hidden",
					backgroundColor: theme.colors.surface,
				}}
			>
				{cardContent}
			</Box>

			{detailedInsight &&
				(detailedInsight.strengths || detailedInsight.weaknesses) && (
					<Box gap={12}>
						<Text variant="titleMedium">Detailed Insights</Text>

						{detailedInsight.strengths &&
							detailedInsight.strengths.length > 0 && (
								<Box gap={8}>
									<Text variant="titleMedium">Strengths</Text>
									{detailedInsight.strengths.map((s) => (
										<Text key={s} variant="bodyMedium">
											• {s}
										</Text>
									))}
								</Box>
							)}

						{detailedInsight.weaknesses &&
							detailedInsight.weaknesses.length > 0 && (
								<Box gap={8}>
									<Text variant="titleMedium">Weaknesses</Text>
									{detailedInsight.weaknesses.map((w) => (
										<Text key={w} variant="bodyMedium">
											• {w}
										</Text>
									))}
								</Box>
							)}
					</Box>
				)}

			{recommendedActions && recommendedActions.length > 0 && (
				<Box gap={12}>
					<Text variant="titleMedium">Recommended</Text>
					{recommendedActions.map((action) => (
						<RecommendedCard key={action.title} {...action} />
					))}
				</Box>
			)}

			{followUpChips && followUpChips.length > 0 && (
				<Box gap={12}>
					<Text variant="titleMedium">Dive deeper</Text>
					<Box flexDirection="row" flexWrap="wrap" gap={8}>
						{followUpChips.map((chip) => (
							<Chip key={chip.label} icon="arrow-right" onPress={chip.onPress}>
								{chip.label}
							</Chip>
						))}
					</Box>
				</Box>
			)}
		</Box>
	);
};
